import * as React from "react";
import { observer, inject } from "mobx-react";
import { styled } from "@mui/material/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Snackbar,
  CircularProgress,
  Box,
  FormControlLabel,
  Checkbox,
  Typography,
  DialogActions,
} from "@mui/material";
import autobind from "autobind-decorator";
import AccountService from "../../service/AccountService";
import { RouterStore } from "mobx-react-router";
import { observable } from "mobx";
import FileService from "../../service/FileService";
import Alert from "@mui/material/Alert";
import firebase from "firebase/compat/app";
import validator from "validator";
import SendIcon from "@mui/icons-material/Send";
import ReplyIcon from "@mui/icons-material/Reply";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import SmsIcon from "@mui/icons-material/Sms";
import EmailIcon from "@mui/icons-material/Email";
import LinkIcon from "@mui/icons-material/Link";
import CodeIcon from "@mui/icons-material/Code";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import UpgradePlanDialog, {
  UpgradeDialogState,
} from "../../pages/subscription/UpgradePlanDialog";
import QRCode from "qrcode.react";
import canvasToImage from "canvas-to-image";
import { withStyles } from "@mui/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import crownIcon from "../../assets/icons/crown.svg";
import premiumIcon from "../../assets/icons/premium.svg";
import { authorizeFeatureAccess, isAuthorizedUser } from "../../util/util";
import UpgradeDialog from "../common/UpgradeDialog";

const StyledDialog = withStyles({
  paper: {
    minWidth: "70%",
  },
})(Dialog);

const nopFunc = () => {};

type ShareDialogProps = {
  account?: AccountService;
  files?: FileService;
  routing?: RouterStore;
  dialogOpen?: boolean;
  onDialogClose?: () => void;
  linkUrl?: string;
  fileId?: string;
  match?: any;
};

type ShareDialogState = {
  color: string;
  position: string;
  width: string;
  height: string;
  shortenUrl: string;
  smsSentConfirmation: boolean;
};

type ShareType =
  | "get_link"
  | "sms"
  | "email"
  | "app_ios"
  | "app_android"
  | "chatbot_code"
  | "qr_code"
  | "embedded_code"
  | "api";

const LinkContainer = styled("div")({
  display: "flex",
  marginBottom: "12px",
  alignItems: "center",
  "@media screen and (max-width: 866px)": {
    Button: {
      flex: "1",
    },
    flexDirection: "column",
    alignItems: "inherit !important",
  },
});

const CustomButton = styled(Button)({
  margin: "4px",
  maxHeight: "40px",
  "@media screen and (max-width: 866px)": {
    flex: "1",
  },
});

const QRCodeContainer = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  columnGap: "40px",
  padding: "1rem 0px",

  "@media screen and (max-width : 455px)": {
    flexDirection: "column",
  },
});

const ButtonContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  "@media screen and (max-width: 766px)": {
    flexDirection: "column",
    alignItems: "inherit !important",
  },
});

const ChatbotContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  margin: "16px 24px",
  "@media screen and (max-width : 768px)": {
    flexDirection: "column",
  },
});

const ButtonLink = styled("div")({
  display: "flex",
  alignItems: "center",
  "@media screen and (max-width : 492px)": {
    flexDirection: "column",
    alignItems: "inherit !important",
  },
});

const CrownIcon = () => (
  <img
    src={crownIcon}
    alt=""
    style={{
      position: "absolute",
      right: "20px",
      height: "20px",
    }}
  />
);

@inject("account")
@inject("files")
@inject("routing")
@observer
export default class ShareDialog extends React.Component<
  ShareDialogProps,
  ShareDialogState
> {
  state: ShareDialogState = {
    color: "#0a65ff",
    position: "bottom_right",
    width: "800px",
    height: "400px",
    shortenUrl: "",
    smsSentConfirmation: false,
  };

  _upgradeDialogState = new UpgradeDialogState();
  @observable
  currentSelected: ShareType = "get_link";

  @observable
  snackMessage = "";

  @observable
  recipient = "";

  @observable
  recipientValidationError = " ";

  @observable
  upgradeDialogOpen: boolean = false;

  @observable
  subjectValidationError = " ";

  @observable
  messageValidationError = " ";

  @observable
  messageText = "";

  @observable
  messageSubject = "";

  @observable
  loading = false;

  @observable
  private canvaRef = React.createRef<HTMLCanvasElement>();

  // @observable
  // refValue : <HTMLCanvasElement>();

  @autobind
  gotoPlan() {
    var { routing } = this.props;
    routing.push(`/plan`);
  }

  @observable
  switchShareType(type: ShareType) {
    if (this.currentSelected == type) {
      return;
    }

    let { linkUrl } = this.props;
    let clinicName = this.props.account.getCurrentClinicName();
    const { shortenUrl } = this.state;
    const longUrl = `https://app.flowcharts.ai${linkUrl || ""}`;

    this.recipient = "";
    this.recipientValidationError = " ";
    this.messageValidationError = " ";
    this.subjectValidationError = " ";
    this.messageText = `Hello, this is ${clinicName}, may you kindly complete this survey for me?\n\n${
      shortenUrl ? shortenUrl : longUrl
    }${
      type === "sms"
        ? `\n\nMsg & data rates may apply. Reply STOP to end messages.`
        : ""
    }`;
    this.messageSubject = `Survey from ${clinicName}`;
    this.currentSelected = type;
  }

  async copyToClipboard(str: string, type: string) {
    await navigator.clipboard.writeText(str);
    this.snackMessage = `${type} copied to clipboard`;
  }

  async sendSurvey(type: string) {
    let { fileId, files, account } = this.props;
    fileId = fileId || "";

    let hasValidationError = false;
    this.recipientValidationError = " ";
    this.subjectValidationError = " ";
    this.messageValidationError = " ";
    this.loading = true;

    let subject = this.messageSubject.replace(/\s/g, "");
    let message = this.messageText.replace(/\s/g, "");

    if (message == "") {
      this.messageValidationError = "Message can not be empty.";
      hasValidationError = true;
    }

    if (type == "sms") {
      var smsList = this.recipient.replace(/\s/g, "").split(",");
      smsList = smsList.map((it) => (it.charAt(0) == "+" ? it : `+1${it}`));
      smsList = smsList.filter((it, index) => smsList.indexOf(it) === index);

      if (smsList.findIndex((it) => !validator.isMobilePhone(it)) != -1) {
        this.recipientValidationError = "Please enter valid phone numbers";
        hasValidationError = true;
      }
    }

    if (type == "email") {
      var emailList = this.recipient.replace(/\s/g, "").split(",");
      emailList = emailList.filter(
        (it, index) => emailList.indexOf(it) === index
      );

      if (emailList.findIndex((it) => !validator.isEmail(it)) != -1) {
        this.recipientValidationError = "Please enter valid email ids";
        hasValidationError = true;
      }

      if (subject == "") {
        this.subjectValidationError = "Subject can not be empty.";
        hasValidationError = true;
      }
    }

    if (hasValidationError) {
      this.loading = false;
      return;
    }

    let fileData = await files.loadFileData(fileId);
    let fileName = await files.loadFileName(fileId);

    if (fileData == "UNKNOWN_ERROR" || fileName == "UNKNOWN_ERROR") {
      let errm = "Something went wrong. Please try again.";
      if (type == "sms") {
        this.recipientValidationError = errm;
      }

      if (type == "email") {
        this.recipientValidationError = errm;
      }
      this.loading = false;
      return;
    }

    const funcs = firebase.functions();
    const sendFunc = funcs.httpsCallable("sendSurvey2");

    let recipientList = type == "sms" ? smsList : emailList;

    let promises = recipientList.map((recipient) =>
      sendFunc({
        type: type,
        recipient: recipient,
        fileData: fileData,
        flowId: fileId,
        flowName: fileName,
        clinic: account.currentClinic,
        userId: account.user.uid,
        messageText: this.messageText,
        messageSubject: this.messageSubject,
      })
    );

    let result = await Promise.all(promises);

    this.snackMessage = `Survey sent via ${type}.`;
    this.loading = false;
  }

  @autobind
  openQuestionnaire() {
    var { linkUrl } = this.props;
    window.open(linkUrl, "_blank");
  }

  renderGetLink() {
    let { linkUrl } = this.props;
    linkUrl = `https://app.flowcharts.ai${linkUrl ? linkUrl : ""}`;

    let copyLink = () => this.copyToClipboard(linkUrl, "Link");

    return (
      <LinkContainer>
        <TextField
          label="Link URL"
          defaultValue={this.state.shortenUrl ? this.state.shortenUrl : linkUrl}
          size="small"
          InputProps={{ readOnly: true }}
          variant="outlined"
          style={{ flexGrow: 1 }}
        />

        <ButtonLink>
          <CustomButton variant="contained" color="primary" onClick={copyLink}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <FileCopyIcon style={{ marginRight: "8px" }} />
              <span>Copy Link</span>
            </div>
          </CustomButton>
          <CustomButton
            variant="contained"
            color="primary"
            onClick={this.openQuestionnaire}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <ReplyIcon style={{ marginRight: "8px" }} />
              <span>Goto</span>
            </div>
          </CustomButton>
        </ButtonLink>
      </LinkContainer>
    );
  }

  renderSendSurvey() {
    let introText = `Please enter up to 10 ${
      this.currentSelected == "sms" ? "phone numbers" : "email addresses"
    } separated by a comma (,).`;
    let labelText =
      this.currentSelected == "sms" ? "Phone Number(s)" : "Email Address(es)";

    return (
      <div style={{ marginBottom: "12px" }}>
        <p style={{ marginBottom: "10px" }}> {introText} </p>
        <Box display="flex">
          <TextField
            label={labelText}
            value={this.recipient}
            variant="outlined"
            size="small"
            onChange={(ev) => (this.recipient = ev.target.value)}
            error={this.recipientValidationError != " "}
            {...(this.recipientValidationError !== " " && {
              helperText: this.recipientValidationError,
            })}
            fullWidth
            disabled={this.loading}
          />

          <Button
            style={{ marginLeft: "8px", maxHeight: "40px" }}
            variant="contained"
            color="primary"
            onClick={() => this.sendSurvey(this.currentSelected)}
            disabled={
              this.loading ||
              (this.currentSelected === "sms" &&
                !this.state.smsSentConfirmation)
            }
          >
            <Box display="flex" alignItems="center">
              {!this.loading && <SendIcon style={{ marginRight: "10px" }} />}
              {this.loading && (
                <CircularProgress
                  size={18}
                  style={{ marginRight: "8px", color: "rgba(0, 0, 0, 0.6)" }}
                />
              )}
              <span>Send</span>
            </Box>
          </Button>
        </Box>
        {this.currentSelected === "sms" && (
          <Box display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  checked={this.state.smsSentConfirmation}
                  onChange={(_, checked) =>
                    this.setState({
                      ...this.state,
                      smsSentConfirmation: checked,
                    })
                  }
                />
              }
              label="Confirm that customer(s) have consented to receive text messages, including links."
            />
          </Box>
        )}

        {this.currentSelected == "email" && (
          <TextField
            label="Subject"
            value={this.messageSubject}
            variant="outlined"
            size="small"
            onChange={(ev) => (this.messageSubject = ev.target.value)}
            error={this.subjectValidationError != " "}
            helperText={this.subjectValidationError}
            fullWidth
            style={{ marginTop: "12px" }}
            disabled={this.loading}
          />
        )}

        <TextField
          label="Message Text"
          value={this.messageText}
          variant="outlined"
          size="small"
          onChange={(ev) => (this.messageText = ev.target.value)}
          error={this.messageValidationError != " "}
          helperText={this.messageValidationError}
          fullWidth
          multiline
          style={{ marginTop: "12px" }}
          disabled={this.loading}
        />
      </div>
    );
  }

  openApp(appType: string) {
    let links = {
      ios: "https://apps.apple.com/us/app/flowcharts-ai/id1513259791",
      android:
        "https://play.google.com/store/apps/details?id=com.flowcharts.ai&hl=en_CA&gl=US",
    };

    window.open(links[appType], "_blank");
  }

  codePosition() {
    if (this.state.position === "bottom_right")
      return "bottom: 20px; right: 30px; align-items: flex-end;flex-direction: column";

    if (this.state.position === "bottom_left")
      return "bottom: 20px; left: 30px; align-items: flex-start;flex-direction: column";

    if (this.state.position === "top_right")
      return "top: 20px; right: 30px; align-items: flex-end;flex-direction: column-reverse;";

    if (this.state.position === "top_left")
      return "top: 20px; left: 30px; align-items: flex-start;flex-direction:column-reverse;";
  }

  renderChatbotCode(color: any): string {
    let { linkUrl } = this.props;
    const colorSelected = this.state.color.split("#")[1];
    linkUrl = `https://app.flowcharts.ai${linkUrl || ""}`;
    const chatUrl = `${linkUrl.replace(
      "questionnaire",
      "chat"
    )}/${colorSelected}`;

    return `<div
    class="container"
    style="
      width: fit-content;
      position: fixed;
      width: 360px;
      display: flex;
      flex-direction: column;
      z-index:100000;
      ${this.codePosition()}
    "
  >
    <iframe
    id="box"
    src=${chatUrl}
    width="360"
    height="550"
    name="box"
    style="display: none; border-radius: 8px; border: none; z-index:100000;"
  ></iframe>
    <div
      id="btn"fix chatbot mobile
      onclick="handleToggle()"
      style="
        width: 50px;
        height: 50px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        background-color: ${color};
        margin: 10px;
        cursor: pointer;
        z-index:100000;
      "
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        height="24px"
        viewBox="0 0 24 24"
        width="24px"
        fill="#FFFFFF"
      >
        <path d="M0 0h24v24H0V0z" fill="none" />
        <path
          d="M15 4v7H5.17l-.59.59-.58.58V4h11m1-2H3c-.55 0-1 .45-1 1v14l4-4h10c.55 0 1-.45 1-1V3c0-.55-.45-1-1-1zm5 4h-2v9H6v2c0 .55.45 1 1 1h11l4 4V7c0-.55-.45-1-1-1z"
        />
      </svg>
    </div>
  </div>
  <script>
  window.onload = getData;
  const iframe = document.getElementById("box");
  const btn = document.getElementById("btn");
  function handleToggle() {
    iframe.style.display === "none"
      ? (iframe.style.display = "block")
      : (iframe.style.display = "none");
  }

  function getData() {
    var eventMethod = window.addEventListener
      ? "addEventListener"
      : "attachEvent";
    var eventer = window[eventMethod];
    var messageEvent = eventMethod === "attachEvent" ? "onmessage" : "message";
    eventer(messageEvent, (e) => {
      const { complete } = e.data;
      if (complete) {
        iframe.style.display = "none";
        btn.style.display = "none";
      }
    });
  }
  </script>
  `;
  }

  copyChatbotCode() {
    this.copyToClipboard(
      this.renderChatbotCode(this.state.color),
      "Chatbot Code"
    );
  }

  renderEmbeddedCode(width: string, height: string): string {
    let { linkUrl } = this.props;
    linkUrl = `https://app.flowcharts.ai${linkUrl || ""}`;
    const embeddedUrl = `${linkUrl.replace("questionnaire", "embedded")}`;
    return `<iframe id ='embeddedCode' width='${width}' height='${height}' src='${embeddedUrl}'></iframe>
    <script >
  window.onload = getData;
  function getData() {
  var eventMethod = window.addEventListener
      ? "addEventListener"
      : "attachEvent";
    var eventer = window[eventMethod];
    var messageEvent = eventMethod === "attachEvent" ? "onmessage" : "message";
    eventer(messageEvent, (e) => {
      const { surveyClosed } = e.data;
      if (surveyClosed) {
        window.close()
      }
    });
  }
</script>
      `;
  }

  copyEmbeddedCode() {
    this.copyToClipboard(
      this.renderEmbeddedCode(this.state.width, this.state.height),
      "Embedded Code"
    );
  }

  @autobind
  downloadQRCode() {
    let { linkUrl } = this.props;
    let clinicName = this.props.account.getCurrentClinicName();
    linkUrl = `https://app.flowcharts.ai${linkUrl || ""}`;
    const ref = window.document.getElementById("canvaID");
    canvasToImage(ref, {
      name: "QR Questionnaire",
      type: "jpg",
      quality: 1,
    });
  }

  @autobind
  paidFeatureHandler(type: ShareType) {
    const userType = this.props.account.userData.userType;
    if (userType !== "FREE_USER") {
      this.switchShareType(type);
    } else {
      this._upgradeDialogState.open = true;
    }
  }

  async componentDidMount() {
    const { files, account } = this.props;
    let linkUrl = "";
    const { id } = this.props.match.params;
    this.props.files.setChatbotColor(this.state.color);
    if (account.user?.uid) {
      linkUrl = `/questionnaire/${account.user.uid}/${account.currentClinic}/${id}`;
      await files.checkShortenUrl(linkUrl, id);
      const shortUrl = await files.getShortenUrl(id);
      this.setState({ ...this.state, shortenUrl: shortUrl });
    }
    // this.refValue = this.canvaRef.current;
  }
  renderButtons() {
    let { linkUrl } = this.props;
    const userData = this.props.account.userData;
    var userType = this.props.account.userData.userType;
    linkUrl = `https://app.flowcharts.ai${linkUrl || ""}`;

    let copyLink = () => this.copyToClipboard(linkUrl, "Link");
    return (
      <>
        <ButtonContainer>
          <Button
            style={{
              margin: "8px",
              backgroundColor: "#6c757d",
              minWidth: "120px",
              flex: "1",
            }}
            variant="contained"
            color="primary"
            onClick={() => this.switchShareType("get_link")}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <LinkIcon style={{ marginRight: "8px" }} />
              <span>Get Link</span>
            </div>
          </Button>
          <Button
            style={{
              margin: "8px",
              backgroundColor: "#626745",
              minWidth: "120px",
              flex: "1",
            }}
            variant="contained"
            color="primary"
            onClick={() => this.switchShareType("qr_code")}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <ForumRoundedIcon style={{ marginRight: "8px" }} />
              QR Code
            </div>
          </Button>
          <QRCode
            value={linkUrl}
            level="H"
            id="canvaID"
            includeMargin={true}
            size={256}
            style={{ display: "none" }}
          />

          <Button
            style={{
              margin: "8px",
              backgroundColor: "#3b9830",
              minWidth: "120px",
              flex: "1",
            }}
            variant="contained"
            color="primary"
            onClick={() =>
              authorizeFeatureAccess(
                userData,
                () => (this.upgradeDialogOpen = true),
                () => this.switchShareType("chatbot_code")
              )
            }
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <ForumRoundedIcon style={{ marginRight: "8px" }} />
              <span>Website Widget</span>
              {!isAuthorizedUser(userData) && <CrownIcon />}
            </div>
          </Button>
          <Button
            style={{
              margin: "8px",
              backgroundColor: "#4b4668",
              minWidth: "120px",
              flex: "1",
            }}
            variant="contained"
            color="primary"
            onClick={() =>
              authorizeFeatureAccess(
                userData,
                () => (this.upgradeDialogOpen = true),
                () => this.switchShareType("embedded_code")
              )
            }
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <CodeIcon style={{ marginRight: "8px" }} />
              <span>Embedded Code</span>
              {!isAuthorizedUser(userData) && <CrownIcon />}
            </div>
          </Button>
        </ButtonContainer>
        <ButtonContainer>
          {/* <Button
            style={{
              margin: '8px',
              backgroundColor: '#4b4668',
              minWidth: '120px',
              flex: '1',
            }}
            variant="contained"
            color="primary"
            onClick={() => this.switchShareType('api')}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <CloudDownloadIcon style={{ marginRight: '8px' }} />
              <span>API</span>
            </div>
          </Button> */}
          <Button
            style={{
              margin: "8px",
              backgroundColor: "#563d7c",
              minWidth: "120px",
              flex: "1",
            }}
            variant="contained"
            color="primary"
            onClick={() =>
              authorizeFeatureAccess(
                userData,
                () => (this.upgradeDialogOpen = true),
                () => this.switchShareType("sms")
              )
            }
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <SmsIcon style={{ marginRight: "8px" }} />
              <span>SMS</span>
              {!isAuthorizedUser(userData) && <CrownIcon />}
            </div>
          </Button>
          <Button
            style={{
              margin: "8px",
              backgroundColor: "rgb(23, 172, 74)",
              minWidth: "120px",
              flex: "1",
            }}
            variant="contained"
            color="primary"
            onClick={() =>
              authorizeFeatureAccess(
                userData,
                () => (this.upgradeDialogOpen = true),
                () => this.switchShareType("email")
              )
            }
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <EmailIcon style={{ marginRight: "8px" }} />
              <span>Email</span>
              {!isAuthorizedUser(userData) && <CrownIcon />}
            </div>
          </Button>
          <Button
            style={{
              margin: "8px",
              backgroundColor: "#343a40",
              minWidth: "120px",
              flex: "1",
            }}
            onClick={() =>
              authorizeFeatureAccess(
                userData,
                () => (this.upgradeDialogOpen = true),
                () => this.openApp("ios")
              )
            }
            variant="contained"
            color="primary"
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <AppleIcon style={{ marginRight: "8px" }} />
              <span>iOS</span>
              {!isAuthorizedUser(userData) && <CrownIcon />}
            </div>
          </Button>
          <Button
            style={{
              margin: "8px",
              backgroundColor: "#007bff",
              minWidth: "120px",
              flex: "1",
            }}
            onClick={() =>
              authorizeFeatureAccess(
                userData,
                () => (this.upgradeDialogOpen = true),
                () => this.openApp("android")
              )
            }
            variant="contained"
            color="primary"
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <AndroidIcon style={{ marginRight: "8px" }} />
              <span>Android</span>
              {!isAuthorizedUser(userData) && <CrownIcon />}
            </div>
          </Button>
        </ButtonContainer>
      </>
    );
  }
  handleChange(event: any) {
    this.setState({ ...this.state, [event.target.name]: event.target.value });
    if (event.target.name == "color") {
      this.props.files.setChatbotColor(event.target.value);
    }
  }

  renderChatbot() {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <h1
          style={{
            fontSize: "22px",
            margin: "10px 0",
            lineHeight: "inherit",
            fontWeight: "inherit",
          }}
        >
          Website Chatbot
        </h1>
        <ChatbotContainer>
          <FormControl variant="outlined" style={{ margin: "5px 0" }}>
            <InputLabel id="color">Color</InputLabel>
            <Select
              labelId="color"
              id="color"
              name="color"
              value={this.state.color}
              onChange={(ev) => this.handleChange(ev)}
              label="Color"
              style={{ width: "150px" }}
            >
              <MenuItem value="#0a65ff">Blue</MenuItem>
              <MenuItem value="#4CAF50">Green</MenuItem>
              <MenuItem value="#F44336">Red</MenuItem>
              <MenuItem value="#FF9800">Orange</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" style={{ margin: "5px 0" }}>
            <InputLabel id="position">Position</InputLabel>
            <Select
              labelId="position"
              id="position"
              name="position"
              value={this.state.position}
              onChange={(ev) => this.handleChange(ev)}
              label="Position"
              style={{ width: "150px" }}
            >
              <MenuItem value="top_right">Top Right</MenuItem>
              <MenuItem value="top_left">Top Left</MenuItem>
              <MenuItem value="bottom_right">Bottom Right</MenuItem>
              <MenuItem value="bottom_left">Bottom Left</MenuItem>
            </Select>
          </FormControl>
          <Button
            style={{
              margin: "8px",
              backgroundColor: "#0a7b96",
              minWidth: "120px",
            }}
            onClick={() => this.copyChatbotCode()}
            variant="contained"
            color="primary"
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <FileCopyIcon style={{ marginRight: "8px" }} />
              <span>Copy Code</span>
            </div>
          </Button>
        </ChatbotContainer>
        <div style={{ position: "relative", marginBottom: "12px" }}>
          <p
            style={{
              position: "absolute",
              top: "-10px",
              left: "12px",
              backgroundColor: "white",
              padding: "0 8px",
              color: "gray",
            }}
          >
            HTML Code to Embed on your website
          </p>
          <textarea
            style={{
              width: "100%",
              height: "135px",
              padding: "12px 16px",
              borderRadius: "8px",
              border: "1px solid lightgray",
              outline: "none",
            }}
            name="code"
            id="code"
            cols={30}
            rows={4}
            value={this.renderChatbotCode(this.state.color)}
            readOnly
          ></textarea>
        </div>
      </div>
    );
  }

  renderEmbeddedQuestionnaire() {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <h1
          style={{
            fontSize: "22px",
            margin: "10px 0",
            lineHeight: "inherit",
            fontWeight: "inherit",
          }}
        >
          Embedded Code
        </h1>
        <ChatbotContainer>
          <FormControl variant="outlined" style={{ margin: "5px 0" }}>
            <TextField
              id="width"
              name="width"
              label="Width"
              variant="outlined"
              value={this.state.width}
              onChange={(ev) => this.handleChange(ev)}
              style={{ width: "200px" }}
            />
          </FormControl>
          <FormControl variant="outlined" style={{ margin: "5px 0" }}>
            <TextField
              id="height"
              name="height"
              label="Height"
              variant="outlined"
              value={this.state.height}
              onChange={(ev) => this.handleChange(ev)}
              style={{ width: "200px" }}
            />
          </FormControl>
          {/* <FormControl variant="outlined" style={{ margin: '5px 0' }}>
            <InputLabel id="position">Position</InputLabel>
            <Select
              labelId="position"
              id="position"
              name="position"
              value={this.state.position}
              onChange={(ev) => this.handleChange(ev)}
              label="Position"
              style={{ width: '150px' }}
            >
              <MenuItem value="top_right">Top Right</MenuItem>
              <MenuItem value="top_left">Top Left</MenuItem>
              <MenuItem value="bottom_right">Bottom Right</MenuItem>
              <MenuItem value="bottom_left">Bottom Left</MenuItem>
            </Select>
          </FormControl> */}
          <Button
            style={{
              margin: "8px",
              backgroundColor: "#0a7b96",
              minWidth: "120px",
            }}
            onClick={() => this.copyEmbeddedCode()}
            variant="contained"
            color="primary"
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <FileCopyIcon style={{ marginRight: "8px" }} />
              <span>Copy Code</span>
            </div>
          </Button>
        </ChatbotContainer>
        <div style={{ position: "relative", marginBottom: "12px" }}>
          <p
            style={{
              position: "absolute",
              top: "-10px",
              left: "12px",
              backgroundColor: "white",
              padding: "0 8px",
              color: "gray",
            }}
          >
            HTML Code to Embed on your website
          </p>
          <textarea
            style={{
              width: "100%",
              height: "135px",
              padding: "12px 16px",
              borderRadius: "8px",
              border: "1px solid lightgray",
              outline: "none",
            }}
            name="code"
            id="code"
            cols={30}
            rows={4}
            value={this.renderEmbeddedCode(this.state.width, this.state.height)}
            readOnly
          ></textarea>
        </div>
      </div>
    );
  }

  renderSnackbar() {
    return (
      <Snackbar
        open={this.snackMessage != ""}
        autoHideDuration={5000}
        onClose={() => (this.snackMessage = "")}
      >
        <div>
          <Alert
            elevation={6}
            variant="filled"
            severity="info"
            onClose={() => (this.snackMessage = "")}
          >
            {this.snackMessage}
          </Alert>
        </div>
      </Snackbar>
    );
  }

  renderQRCode() {
    let { linkUrl } = this.props;
    linkUrl = `https://app.flowcharts.ai${linkUrl || ""}`;
    return (
      <QRCodeContainer>
        <QRCode
          value={linkUrl}
          level="H"
          includeMargin={true}
          size={128}
          style={{ height: "128px", width: "128px" }}
        />
        <Button
          onClick={this.downloadQRCode}
          variant="contained"
          color="primary"
          style={{
            margin: "8px",
            maxHeight: "40px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <FileCopyIcon style={{ marginRight: "8px" }} />
            <span>Download</span>
          </div>
        </Button>
      </QRCodeContainer>
    );
  }

  // @autobind
  // renderAPI() {
  //   const apiKey = this.props.fileId;
  //   const privateKey = this.props.account.user.uid;
  //   const apiUrl =
  //     'https://us-central1-ai-flowchart.cloudfunctions.net/api/questionnaire?API_KEY=YOUR_API_KEY&PRIVATE_KEY=YOUR_PRIVATE_KEY';
  //   return (
  //     <div style={{ display: 'flex', flexDirection: 'column' }}>
  //       <h1
  //         style={{
  //           fontSize: '22px',
  //           margin: '10px 0',
  //           lineHeight: 'inherit',
  //           fontWeight: 'inherit',
  //         }}
  //       >
  //         Responses API
  //       </h1>
  //       <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
  //         <FormControl variant="outlined" style={{ flex: '1', margin: '20px' }}>
  //           <InputLabel htmlFor="apiKey">API KEY</InputLabel>
  //           <OutlinedInput
  //             id="apiKey"
  //             type={this.state.showApiKey ? 'text' : 'password'}
  //             value={apiKey}
  //             inputProps={{
  //               readOnly: true,
  //             }}
  //             endAdornment={
  //               <InputAdornment position="end">
  //                 <IconButton
  //                   aria-label="toggle password visibility"
  //                   onClick={() =>
  //                     this.setState({
  //                       ...this.state,
  //                       showApiKey: !this.state.showApiKey,
  //                     })
  //                   }
  //                   // onMouseDown={handleMouseDownPassword}
  //                   edge="end"
  //                 >
  //                   {this.state.showApiKey ? <VisibilityOff /> : <Visibility />}
  //                 </IconButton>
  //                 <IconButton
  //                   aria-label="toggle password visibility"
  //                   onClick={() => this.copyToClipboard(apiKey, 'API Key')}
  //                   // onMouseDown={handleMouseDownPassword}
  //                   edge="end"
  //                 >
  //                   <FileCopyIcon />
  //                 </IconButton>
  //               </InputAdornment>
  //             }
  //             label="API KEY"
  //           />
  //         </FormControl>
  //         <FormControl variant="outlined" style={{ flex: '1', margin: '20px' }}>
  //           <InputLabel htmlFor="privateKey">PRIVATE KEY</InputLabel>
  //           <OutlinedInput
  //             id="privateKey"
  //             type={this.state.showPrivateKey ? 'text' : 'password'}
  //             value={privateKey}
  //             inputProps={{
  //               readOnly: true,
  //             }}
  //             endAdornment={
  //               <InputAdornment position="end">
  //                 <IconButton
  //                   aria-label="toggle password visibility"
  //                   onClick={() =>
  //                     this.setState({
  //                       ...this.state,
  //                       showPrivateKey: !this.state.showPrivateKey,
  //                     })
  //                   }
  //                   // onMouseDown={handleMouseDownPassword}
  //                   edge="end"
  //                 >
  //                   {this.state.showPrivateKey ? (
  //                     <VisibilityOff />
  //                   ) : (
  //                     <Visibility />
  //                   )}
  //                 </IconButton>
  //                 <IconButton
  //                   aria-label="toggle password visibility"
  //                   onClick={() =>
  //                     this.copyToClipboard(privateKey, 'Private Key')
  //                   }
  //                   // onMouseDown={handleMouseDownPassword}
  //                   edge="end"
  //                 >
  //                   <FileCopyIcon />
  //                 </IconButton>
  //               </InputAdornment>
  //             }
  //             label="PRIVATE KEY"
  //           />
  //         </FormControl>
  //       </div>
  //       <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
  //         <FormControl variant="outlined" style={{ flex: '1', margin: '20px' }}>
  //           <InputLabel htmlFor="endpoint url">
  //             FlowCharts AI's base endpoint
  //           </InputLabel>
  //           <OutlinedInput
  //             id="endpoint url"
  //             type={'text'}
  //             value={apiUrl}
  //             inputProps={{
  //               readOnly: true,
  //             }}
  //             endAdornment={
  //               <InputAdornment position="end">
  //                 <IconButton
  //                   aria-label="copy endpoint url"
  //                   onClick={() => this.copyToClipboard(apiUrl, 'API URL')}
  //                   edge="end"
  //                 >
  //                   <FileCopyIcon />
  //                 </IconButton>
  //               </InputAdornment>
  //             }
  //             label="FlowCharts AI's base endpoint"
  //           />
  //         </FormControl>
  //       </div>
  //     </div>
  //   );
  // }

  render() {
    let { dialogOpen, onDialogClose } = this.props;
    onDialogClose = onDialogClose || nopFunc;

    return (
      <StyledDialog open={dialogOpen} onClose={onDialogClose}>
        <DialogTitle>Choose How You Share!</DialogTitle>
        <DialogContent>
          {/* <UpgradePlanDialog
            upgradeOpen={this._upgradeDialogState}
            upgradeContent={"upgrade"}
          /> */}
          <UpgradeDialog
            upgradeDialogOpen={this.upgradeDialogOpen}
            onClose={() => (this.upgradeDialogOpen = false)}
            onConfirm={this.gotoPlan}
          />
          {this.currentSelected == "chatbot_code" && this.renderChatbot()}
          {this.currentSelected == "get_link" && this.renderGetLink()}
          {this.currentSelected == "sms" && this.renderSendSurvey()}
          {this.currentSelected == "email" && this.renderSendSurvey()}
          {this.currentSelected == "qr_code" && this.renderQRCode()}
          {this.currentSelected == "embedded_code" &&
            this.renderEmbeddedQuestionnaire()}
          {/* {this.currentSelected == 'api' && this.renderAPI()} */}
          {this.renderButtons()}
          {this.renderSnackbar()}
        </DialogContent>
      </StyledDialog>
    );
  }
}
